import type { ReactNode } from 'react';
import type { Size } from '../engine/types';
import { useI18n, type Lang } from '../i18n';
import { nextThemePref, type Scheme, type ThemePref } from '../theme/theme';
import { Icon, Wordmark } from '../ui/Icon';
import { Switch } from './Findings';
import { Menu } from './Menu';
import { SizeChips } from './SizeChips';
import { ThemeToggle, themeLabel } from './ThemeToggle';

interface Props {
  onLang: (lang: Lang) => void;
  /** Theme choice (light by default) and the scheme it shows. */
  theme: ThemePref;
  themeScheme: Scheme;
  onTheme: (pref: ThemePref) => void;
  sizes: Size[];
  selectedSize: number | null;
  onSize: (s: number | null) => void;
  enginePill: ReactNode;
  onCopyLink: () => void;
  onDownload: () => void;
  canDownload: boolean;
  onClear: () => void;
  onPrint: () => void;
  readerMode: boolean;
  onReaderMode: (v: boolean) => void;
  canRead: boolean;
  compact: boolean;
  onCompact: (v: boolean) => void;
}

/** The header: brand, the sizes and « Ma taille », then quiet utilities; a rib band under it. */
export function TopBar(props: Props) {
  const { t, lang } = useI18n();
  return (
    <header data-app-header className="relative z-20 bg-surface">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 sm:px-6 lg:h-14 lg:flex-nowrap lg:py-0">
        <div className="order-1 mr-2 shrink-0">
          <Wordmark tagline={t('app.tagline')} level={t('app.level')} />
        </div>

        <div className="order-5 flex max-w-full items-center gap-2 sm:order-3 lg:ml-4">
          {props.sizes.length > 1 && <SizeChips sizes={props.sizes} selected={props.selectedSize} onSelect={props.onSize} />}
          {props.canRead && (
            <button
              type="button"
              aria-pressed={props.readerMode}
              onClick={() => props.onReaderMode(!props.readerMode)}
              title={t('top.mySizeHint')}
              data-testid="reader-toggle"
              className={`inline-flex h-8 shrink-0 items-center gap-2 rounded-pill px-3 text-xs font-semibold transition-colors ${
                props.readerMode ? 'bg-accent text-on-accent' : 'text-muted hover:bg-fg/[0.05] hover:text-fg'
              }`}
            >
              <Icon name="book" size={14} />
              {t('top.mySize')}
            </button>
          )}
        </div>

        {/* One engine status only (a single live region); CSS order moves it on small screens. */}
        <div className="order-6 max-w-full sm:order-4 lg:ml-auto">{props.enginePill}</div>

        <div className="order-2 ml-auto flex items-center gap-1 sm:order-5 lg:ml-0">
          <div role="group" aria-label={t('top.lang')} className="flex rounded-pill bg-sunken p-0.5">
            {(['fr', 'en'] as const).map((l) => (
              <button
                key={l}
                type="button"
                aria-pressed={lang === l}
                title={l === 'fr' ? 'Français' : 'English'}
                onClick={() => props.onLang(l)}
                className={`h-7 rounded-pill px-2.5 text-2xs font-bold uppercase tracking-[0.08em] ${
                  lang === l ? 'bg-surface text-fg shadow-soft-sm' : 'text-subtle hover:text-fg'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          {/* Phones: in the « … » menu (with « auto » the row would wrap). */}
          <ThemeToggle pref={props.theme} scheme={props.themeScheme} onChange={props.onTheme} className="ml-1 hidden sm:inline-flex" />
          <Switch
            className="ml-2 hidden md:inline-flex"
            checked={props.compact}
            onChange={props.onCompact}
            label={t('top.compact')}
            testId="compact-toggle"
          />
          <button type="button" className="btn-quiet ml-1" onClick={props.onCopyLink} aria-label={t('top.copyLink')} title={t('top.copyLink')}>
            <Icon name="link" />
            <span className="hidden xl:inline">{t('top.copyLink')}</span>
          </button>
          <Menu
            label={t('top.more')}
            testId="more-menu"
            items={[
              { id: 'print', label: props.readerMode ? t('top.printReader') : t('top.print'), icon: 'print', onSelect: props.onPrint, testId: 'menu-print' },
              {
                id: 'download',
                label: t('top.download'),
                icon: 'download',
                onSelect: props.onDownload,
                disabled: !props.canDownload,
                testId: 'menu-download',
              },
              {
                id: 'compact',
                label: t('top.compact'),
                hint: t('top.compactHint'),
                checked: props.compact,
                onSelect: () => props.onCompact(!props.compact),
                testId: 'menu-compact',
              },
              {
                id: 'theme',
                label: themeLabel(t, props.theme),
                hint: t('theme.menuHint'),
                icon: props.themeScheme === 'dark' ? 'moon' : 'sun',
                onSelect: () => props.onTheme(nextThemePref(props.theme)),
                testId: 'menu-theme',
              },
              { id: 'clear', label: t('top.clear'), hint: t('top.clearTitle'), icon: 'trash', onSelect: props.onClear, testId: 'menu-clear' },
            ]}
          />
        </div>
      </div>
      <div className="rib-band" aria-hidden="true" />
    </header>
  );
}
